"use client";

import {
  PrizeWheel,
  type PrizeWheelRef,
} from "@/components/ui/custom/prize-wheel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  PROMOTIONAL_PRIZES_STORAGE_KEY,
  PROMOTIONAL_WALLETS,
} from "@/constants";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken } from "@/hooks/useAccessToken";
import { authorizedGet, authorizedPost } from "@/utils/request";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PRIZE_IMAGE_MAP,
  PRIZE_SEGMENT_ORDER,
  getPrizeClaimCopy,
  getTierName,
  getTierOverlayColor,
  mapTierToSegmentColour,
} from "./PrizeWheelUtils";
import type { PrizeWheelSegmentData } from "./types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spinsAvailable?: number;
  onPrizeWon?: () => void;
};

type ApiPrize = { amount: number; name: string; tier: number };
type ApiResponse = { available_prizes: ApiPrize[] };

export function PrizeWheelV2Modal({
  open,
  onOpenChange,
  spinsAvailable = 0,
  onPrizeWon,
}: Props) {
  const { trackV3 } = useAnalytics();
  const wheelRefDesktop = useRef<PrizeWheelRef>(null);
  const wheelRefMobile = useRef<PrizeWheelRef>(null);
  const spinStateRef = useRef<{
    expectedId: string | null;
    reported: boolean;
    completed: { desktop: boolean; mobile: boolean };
  }>({
    expectedId: null,
    reported: false,
    completed: { desktop: false, mobile: false },
  });
  const [isSpinning, setIsSpinning] = useState(false);
  const [segments, setSegments] = useState<PrizeWheelSegmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"wheel" | "prize">("wheel");
  const [winning, setWinning] = useState<PrizeWheelSegmentData | null>(null);
  const prizeDelayRef = useRef<number | null>(null);
  const [availableSpins, setAvailableSpins] = useState<number>(spinsAvailable);
  const getAccessToken = useAccessToken();
  const { walletAddress } = usePassportProvider();
  type SpinResponse = { prize_name?: string };
  const wheelUiHidden = isSpinning || (mode === "wheel" && winning !== null);
  const bodyFadeClass = useMemo(
    () =>
      `transition-opacity duration-500 ${wheelUiHidden ? "opacity-0 pointer-events-none" : "opacity-100"}`,
    [wheelUiHidden],
  );
  const [overlayKey, setOverlayKey] = useState(0);
  const [videoReady, setVideoReady] = useState(false);

  const handleModalClose = (open: boolean) => {
    if (!open) {
      trackV3({
        object: "Prize_Wheel",
        action: Action.PRESSED,
        extras: {
          mode,
          hadWinning: winning !== null,
          winningPrize: winning?.label,
          winningTier: winning?.tier,
          spinsRemaining: availableSpins,
          isLoggedIn: true,
          action: "closed",
        },
      });
    }
    onOpenChange(open);
  };

  const showOverlay =
    isSpinning || mode === "prize" || (mode === "wheel" && winning !== null);

  useEffect(() => {
    if (!showOverlay) return;
    setVideoReady(false);
    setOverlayKey((k) => k + 1);
  }, [showOverlay]);

  useEffect(() => {
    const load = async () => {
      if (!open || segments.length > 0 || loading) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("Not authenticated");

        const data = await authorizedGet<ApiResponse>(
          "/v2/rewards/spin/available-prizes",
          token,
        );

        const prizeWheelSegmentData: PrizeWheelSegmentData[] = (
          data.available_prizes || []
        ).map((p, i) => {
          const tier = mapTierToSegmentColour(p.tier);

          const imageKey = p.name.toLowerCase() as keyof typeof PRIZE_IMAGE_MAP;
          const imageUrl = PRIZE_IMAGE_MAP[imageKey];
          const url = imageUrl
            ? imageUrl
            : "/icons/prize-wheel/generic-gem.webp";

          return {
            id: `p-${i}`,
            label: p.name,
            tier,
            imageUrl: url,
          };
        });

        const orderedSegments: PrizeWheelSegmentData[] = [];
        for (const prizeName of PRIZE_SEGMENT_ORDER) {
          const segment = prizeWheelSegmentData.find(
            (s) => s.label.toLowerCase() === prizeName.toLowerCase(),
          );
          if (segment) {
            orderedSegments.push(segment);
          }
        }

        for (const segment of prizeWheelSegmentData) {
          if (
            !orderedSegments.some(
              (s) => s.label.toLowerCase() === segment.label.toLowerCase(),
            )
          ) {
            orderedSegments.push(segment);
          }
        }

        const finalSegments = orderedSegments.slice(
          0,
          prizeWheelSegmentData.length,
        );
        setSegments(finalSegments);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load prizes");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, segments.length, loading, getAccessToken]);

  useEffect(() => {
    if (open) {
      trackV3({
        object: "Prize_Wheel",
        action: Action.OPENED,
        extras: {
          spinsAvailable,
        },
      });
    }
  }, [open, trackV3, spinsAvailable]);

  // Reset when modal opens (avoid flashing different screen while closing)
  useEffect(() => {
    if (!open) return;
    setMode("wheel");
    setWinning(null);
    setIsSpinning(false);
    spinStateRef.current.expectedId = null;
    spinStateRef.current.reported = false;
    spinStateRef.current.completed = { desktop: false, mobile: false };
    if (prizeDelayRef.current) {
      clearTimeout(prizeDelayRef.current);
      prizeDelayRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    setAvailableSpins(spinsAvailable);
  }, [spinsAvailable]);

  const fetchAvailableSpins = async (): Promise<number> => {
    try {
      const token = await getAccessToken();
      if (!token) return 0;
      const resp = await authorizedGet<{ availableSpins?: number }>(
        "/v2/rewards/spin/available-spins",
        token,
      );
      return resp.availableSpins || 0;
    } catch {
      return 0;
    }
  };

  const normalize = (value: string | undefined | null) =>
    (value || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "")
      .trim();

  // TODO: Clean up after promotion
  const isPromotionalWallet = (address: string | undefined) => {
    if (!address) return false;
    return PROMOTIONAL_WALLETS.some(
      (addr) => addr.toLowerCase() === address.toLowerCase(),
    );
  };

  // TODO: Clean up after promotion
  const getPrizeTypeFromTier = (
    prize: PrizeWheelSegmentData,
  ): "gems" | "imx" | "physical" => {
    // Tier mapping:
    // Tier 1 (yellow) = Physical prizes (hardware) - use quantity
    // Tier 2 (purple) = High value prizes - use quantity
    // Tier 3 (blue) = Medium value gems/IMX - use amount
    // Tier 4 (green) = Low value gems/IMX - use amount
    // Tier 5 (grey) = Common gems/IMX - use amount

    // Special case: Check for IMX first regardless of tier
    const prizeName = prize.label.toLowerCase();
    if (prizeName.includes("imx")) {
      return "imx";
    }

    // For gems, check the label
    if (prizeName.includes("gem")) {
      return "gems";
    }

    // For physical prizes, check tier
    if (prize.tier === "yellow" || prize.tier === "purple") {
      return "physical"; // Physical hardware prizes and high value items
    }

    return "gems"; // Default to gems for non-physical prizes
  };

  // TODO: Clean up after promotion
  const savePromotionalPrize = (prize: PrizeWheelSegmentData) => {
    if (!walletAddress || !isPromotionalWallet(walletAddress)) return;

    try {
      const stored = localStorage.getItem(PROMOTIONAL_PRIZES_STORAGE_KEY);
      const prizes = stored ? JSON.parse(stored) : {};
      const walletKey = walletAddress.toLowerCase();

      if (!prizes[walletKey]) {
        prizes[walletKey] = [];
      }

      const prizeType = getPrizeTypeFromTier(prize);

      let amount = 1; // Default

      // For IMX and gems, extract amount from name regardless of tier
      if (prizeType === "imx" || prizeType === "gems") {
        if (prize.label.includes("$")) {
          // Extract dollar amount from prize name like "$20 Steam" -> 20
          const match = prize.label.match(/\$(\d+)/);
          amount = match ? Number.parseInt(match[1], 10) : 1;
        } else {
          // Extract amount from prize name like "500 Gems" or "50 IMX" -> 500 or 50
          const match = prize.label.match(/(\d+)/);
          amount = match ? Number.parseInt(match[1], 10) : 1;
        }
      }
      // For physical prizes, use quantity = 1
      else if (prizeType === "physical") {
        amount = 1; // Quantity for physical/high value prizes
      }

      // Convert prize to storage format
      const prizeToStore = {
        amount: amount,
        name: prize.label,
        type: prizeType,
      };

      // Determine the specific category for consolidation
      const getPrizeCategory = (prize: PrizeWheelSegmentData) => {
        const name = prize.label.toLowerCase();

        // Hardware categories
        if (name.includes("playstation 5") || name.includes("ps5"))
          return "ps5";
        if (name.includes("series x")) return "xbox";
        if (name.includes("meta quest 3")) return "meta-quest";
        if (name.includes("switch 2")) return "switch2";

        // Gift cards
        if (name.includes("$")) return "gift-cards";

        // Gems and IMX
        if (prizeType === "gems") return "gems";
        if (prizeType === "imx") return "imx";

        return "other";
      };

      const category = getPrizeCategory(prize);

      // Find existing prize in the same category
      const existingPrizeIndex = prizes[walletKey].findIndex(
        (p: { category: string }) => p.category === category,
      );

      if (existingPrizeIndex !== -1) {
        // Sum the amounts for existing prize in same category
        prizes[walletKey][existingPrizeIndex].amount += amount;
      } else {
        // Add new prize with category
        prizes[walletKey].push({
          ...prizeToStore,
          category: category,
        });
      }

      localStorage.setItem(
        PROMOTIONAL_PRIZES_STORAGE_KEY,
        JSON.stringify(prizes),
      );

      // Trigger refresh of prizes display
      onPrizeWon?.();
    } catch (error) {
      console.error("Failed to save promotional prize:", error);
    }
  };

  // TODO: Clean up after promotion
  const findSegmentByPrizeName = (
    prizeName: string,
  ): PrizeWheelSegmentData | undefined => {
    const prizeNorm = normalize(prizeName);
    if (!prizeNorm) return undefined;

    let match = segments.find((s) => normalize(s.label) === prizeNorm);
    if (match) return match;

    match = segments.find((s) => {
      const ln = normalize(s.label);
      return ln.includes(prizeNorm) || prizeNorm.includes(ln);
    });
    if (match) return match;

    const mapKey = Object.keys(PRIZE_IMAGE_MAP).find(
      (k) => normalize(k) === prizeNorm,
    );
    if (mapKey) {
      const keyNorm = normalize(mapKey);
      match = segments.find((s) => {
        const ln = normalize(s.label);
        return ln === keyNorm || ln.includes(keyNorm) || keyNorm.includes(ln);
      });
    }
    return match;
  };

  const handleWheelSpinningChange = (spinning: boolean) => {
    if (spinning) setIsSpinning(true);
  };

  const handleWheelComplete = (
    source: "desktop" | "mobile",
    seg: PrizeWheelSegmentData,
  ) => {
    if (spinStateRef.current.reported) return;

    if (
      spinStateRef.current.expectedId &&
      seg.id !== spinStateRef.current.expectedId
    ) {
      return;
    }

    spinStateRef.current.completed[source] = true;
    const bothDone =
      spinStateRef.current.completed.desktop &&
      spinStateRef.current.completed.mobile;

    if (!bothDone) return;

    spinStateRef.current.reported = true;
    setIsSpinning(false);
    setWinning(seg);

    // Save prize to localStorage for promotional wallets
    savePromotionalPrize(seg);

    // Track prize won event
    trackV3({
      object: "Prize_Wheel",
      action: Action.COMPLETED,
      extras: {
        prizeName: seg.label,
        prizeId: seg.id,
        prizeTier: seg.tier,
        prizeValue: seg.label,
        spinsRemaining: availableSpins - 1,
        wheelSource: source,
        isLoggedIn: true,
      },
    });

    if (prizeDelayRef.current) {
      clearTimeout(prizeDelayRef.current);
    }

    prizeDelayRef.current = window.setTimeout(() => {
      setMode("prize");
      void fetchAvailableSpins()
        .then(setAvailableSpins)
        .catch(() => {});
      prizeDelayRef.current = null;
    }, 1000);
  };

  const handleSpinPrimary = async () => {
    if (segments.length === 0) return;

    trackV3({
      object: "Prize_Wheel",
      action: Action.PRESSED,
      extras: {
        buttonType: "spin",
        spinsAvailable,
        segmentCount: segments.length,
        isLoggedIn: true,
      },
    });

    try {
      setIsSpinning(true);
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const resp = await authorizedPost<SpinResponse>(
        "/v2/rewards/spin/spin-wheel",
        {},
        token,
      );
      const prizeName = (resp?.prize_name || "").toString();
      const backendTarget = findSegmentByPrizeName(prizeName);
      const chosen =
        backendTarget ?? segments[Math.floor(Math.random() * segments.length)];
      spinStateRef.current.expectedId = chosen.id;
      spinStateRef.current.reported = false;
      spinStateRef.current.completed = { desktop: false, mobile: false };
      wheelRefDesktop.current?.spin(chosen.id);
      wheelRefMobile.current?.spin(chosen.id);
    } catch {
      setIsSpinning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="max-w-[95vw] w-screen md:w-[95vw] h-[95vh] p-0 md:p-0 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 fixed border border-white/10 bg-background/95 shadow-none md:shadow-lg rounded-2xl overflow-hidden [&>button]:z-40 [&>button]:text-white/70 [&>button]:hover:text-white [&>button]:bg-white/10 [&>button]:hover:bg-white/20 [&>button]:rounded-full [&>button]:w-8 [&>button]:h-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center">
        <div className="sticky top-0 z-30 w-full h-[88px] flex flex-col">
          <div className="flex-1 flex items-center px-5">
            <div className="flex items-center gap-5">
              <div className="relative w-12 h-12 rounded-[4px] bg-[#131313] flex items-center justify-center">
                <img
                  src="/icons/favicon-onLight-512x512.png"
                  alt="Immutable"
                  className="w-6 h-6 object-contain"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-[4px] border border-white/10"
                />
              </div>
              <DialogTitle className="text-xs font-bold text-neutral-50">
                Prize Wheel
              </DialogTitle>
            </div>
          </div>
          <div aria-hidden className="w-full h-px bg-white/10 flex-shrink-0" />
        </div>
        <div
          className={`absolute inset-x-0 top-[88px] bottom-0 z-0 pointer-events-none isolate transition-opacity duration-300 ${showOverlay ? "opacity-100" : "opacity-0"}`}
        >
          {showOverlay && (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src={`/video/prize-wheel/spin-prize-overlay.mp4?v=${overlayKey}`}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              key={overlayKey}
              onLoadedData={(e) => {
                setVideoReady(true);
                const vid = e.currentTarget as HTMLVideoElement;
                if (showOverlay && vid.isConnected) {
                  try {
                    vid.currentTime = 0;
                    void vid.play();
                  } catch {}
                }
              }}
            />
          )}
          {mode === "prize" && videoReady && (
            <div
              className="absolute inset-0 pointer-events-none mix-blend-color opacity-40 transition-opacity duration-200"
              style={{
                backgroundColor: getTierOverlayColor(winning?.tier ?? "grey"),
              }}
              aria-hidden
            />
          )}
        </div>

        <div
          className="flex flex-col items-center justify-start gap-2 w-full h-full min-h-[80vh] relative pb-6 px-4 md:px-6 overflow-hidden"
          style={{
            paddingTop: mode === "wheel" ? "0px" : "24px",
          }}
        >
          {mode === "wheel" ? (
            <>
              <div
                className={`text-center z-10 flex-shrink-0 ${bodyFadeClass}`}
              >
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold leading-tight text-neutral-50 m-0">
                  {`You Have ${Math.max(0, spinsAvailable)} Free Spin${Math.max(0, spinsAvailable) === 1 ? "" : "s"}`}
                  <br />
                  Of The $5,000 Prize Wheel!
                </h2>
              </div>
              {/* Desktop wheel */}
              <div
                className={`w-full flex-1 flex justify-center items-center relative z-10 hidden md:flex min-h-0 ${
                  isSpinning ||
                  loading ||
                  segments.length === 0 ||
                  availableSpins <= 0
                    ? "cursor-not-allowed"
                    : "cursor-pointer"
                }`}
                onClick={() => {
                  if (
                    !isSpinning &&
                    !loading &&
                    segments.length > 0 &&
                    availableSpins > 0
                  ) {
                    void handleSpinPrimary();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (
                      !isSpinning &&
                      !loading &&
                      segments.length > 0 &&
                      availableSpins > 0
                    ) {
                      void handleSpinPrimary();
                    }
                  }
                }}
                // biome-ignore lint/a11y/useSemanticElements: Need to maintain flex layout properties that don't work well with button element
                role="button"
                tabIndex={0}
                aria-label="Spin the prize wheel"
                aria-disabled={
                  isSpinning ||
                  loading ||
                  segments.length === 0 ||
                  availableSpins <= 0
                }
              >
                <PrizeWheel
                  ref={wheelRefDesktop}
                  segments={segments}
                  size="xl"
                  className="flex-shrink-0"
                  vwScale={0.82}
                  vhScale={0.62}
                  onSpinComplete={(seg) => handleWheelComplete("desktop", seg)}
                  onSpinningChange={handleWheelSpinningChange}
                />
              </div>
              <div
                className={`w-full flex-1 relative z-10 md:hidden ${
                  isSpinning ||
                  loading ||
                  segments.length === 0 ||
                  availableSpins <= 0
                    ? "cursor-not-allowed"
                    : "cursor-pointer"
                }`}
                onClick={() => {
                  if (
                    !isSpinning &&
                    !loading &&
                    segments.length > 0 &&
                    availableSpins > 0
                  ) {
                    void handleSpinPrimary();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (
                      !isSpinning &&
                      !loading &&
                      segments.length > 0 &&
                      availableSpins > 0
                    ) {
                      void handleSpinPrimary();
                    }
                  }
                }}
                // biome-ignore lint/a11y/useSemanticElements: Need to maintain flex layout properties that don't work well with button element
                role="button"
                tabIndex={0}
                aria-label="Spin the prize wheel"
                aria-disabled={
                  isSpinning ||
                  loading ||
                  segments.length === 0 ||
                  availableSpins <= 0
                }
              >
                <div
                  className="absolute left-0 top-1/2"
                  style={{
                    transform: "translateX(-50%) translateY(-50%) scale(2)",
                    transformOrigin: "center",
                    WebkitMaskImage:
                      isSpinning || winning
                        ? "linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)"
                        : "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 15%, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.1) 85%, rgba(0,0,0,0.05) 100%), linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)",
                    maskImage:
                      isSpinning || winning
                        ? "linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)"
                        : "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 15%, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.1) 85%, rgba(0,0,0,0.05) 100%), linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,1) 100%)",
                    WebkitMaskComposite: "intersect",
                    maskComposite: "intersect",
                  }}
                >
                  <PrizeWheel
                    ref={wheelRefMobile}
                    segments={segments}
                    size="xl"
                    className="flex-shrink-0 max-w-[200vw] max-h-[120vh] w-auto h-auto"
                    onSpinComplete={(seg) => handleWheelComplete("mobile", seg)}
                    onSpinningChange={handleWheelSpinningChange}
                  />
                </div>
              </div>
              <div
                className={`flex items-center justify-center w-full px-6 md:px-0 relative z-30 flex-shrink-0 ${bodyFadeClass}`}
              >
                <button
                  onClick={handleSpinPrimary}
                  disabled={
                    isSpinning ||
                    loading ||
                    segments.length === 0 ||
                    availableSpins <= 0
                  }
                  className="bg-[#f7f7f7] text-[#0d0d0d] flex items-center justify-center gap-2 px-4 py-2 rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] disabled:opacity-50 disabled:cursor-not-allowed h-12"
                  style={{
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    gap: "8px",
                  }}
                  type="button"
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <img
                      src="/assets/icons/pinwheel.svg"
                      alt="Spin icon"
                      className="w-4 h-4 object-contain brightness-0"
                    />
                  </div>
                  <span
                    className="font-medium text-nowrap flex-shrink-0"
                    style={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "#0d0d0d",
                    }}
                  >
                    {isSpinning
                      ? "Spinning..."
                      : `Spins Remaining • ${Math.max(0, availableSpins)}`}
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div
                className={`text-center mt-2 z-10 ${isSpinning ? "opacity-0 transition-opacity duration-500" : "opacity-100"}`}
              >
                <h2 className="text-4xl font-bold leading-[40px] text-neutral-50 text-center">
                  {getTierName(winning?.tier ?? "grey")}
                  <br />
                  {winning?.label}
                </h2>
              </div>
              <div className="w-full flex justify-center items-center relative z-10 mt-3">
                {winning?.imageUrl ? (
                  <img
                    src={winning.imageUrl}
                    alt={winning.label}
                    className="w-[240px] h-[240px] md:w-[320px] md:h-[320px] lg:w-[420px] lg:h-[420px] object-contain drop-shadow-xl"
                  />
                ) : null}
              </div>
              <div
                className={`flex flex-col items-center gap-5 z-30 mt-4 ${isSpinning ? "opacity-0 transition-opacity duration-500" : "opacity-100"}`}
              >
                <div className="flex flex-col gap-1 items-center">
                  <p className="text-sm font-semibold text-neutral-50 text-center leading-5">
                    {getPrizeClaimCopy(
                      winning?.tier ?? "grey",
                      winning?.label ?? "",
                    )}
                  </p>
                </div>
                <div className="flex flex-col gap-2 items-center">
                  <button
                    onClick={async () => {
                      trackV3({
                        object: "Prize_Wheel",
                        action: Action.PRESSED,
                        extras: {
                          buttonType: "spinAgain",
                          previousPrize: winning?.label,
                          previousTier: winning?.tier,
                          spinsAvailable,
                          isLoggedIn: true,
                        },
                      });

                      setOverlayKey((k) => k + 1);
                      setWinning(null);
                      setMode("wheel");
                      setIsSpinning(true); // Set spinning immediately to hide UI
                      await handleSpinPrimary();
                    }}
                    disabled={
                      isSpinning ||
                      loading ||
                      segments.length === 0 ||
                      availableSpins <= 0
                    }
                    type="button"
                    className="bg-[#f7f7f7] text-[#0d0d0d] h-12 px-4 py-2 rounded-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <img
                        src="/assets/icons/pinwheel.svg"
                        alt="Spin icon"
                        className="w-4 h-4 object-contain brightness-0"
                      />
                    </div>
                    <span className="text-sm font-medium leading-5 whitespace-nowrap">
                      {availableSpins > 0
                        ? `Spin Again • ${availableSpins}`
                        : "No Spins Remaining"}
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PrizeWheelV2Modal;
